import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import withCanvasContainer, { WithCanvasContainerProps } from './withCanvasContainer';
import WellDropImageLoader from '../components/WellDropImageLoader';
import { useScores } from 'redux/hooks/searchnFilter';
import { useRMSelector } from 'redux/hooks';
import { selectHideAutoScores, selectScoreGroups } from 'redux/selectors/StateSelectors';
import { Group } from 'react-konva';
import { HitRectangles } from './hitRectangles';
import Scores from 'components/plateviewer/plate/Scores';
import { filterScores } from '../utils';
import { compareWithScoreGroupId } from 'utils/Global';

type Props = WithCanvasContainerProps;

const ImageCanvas = (props: Props) => {
  const { wellRectangles, wellRects, searchResult } = props;
  const scores = useScores();
  const loadedImagesRef = useRef(0);

  const scoresGroup = useRMSelector(selectScoreGroups);
  const imageGroupRef = useRef(null);
  const scoresGroupRef = useRef(null);

  const handleOnImageLoad = useCallback(() => {
    loadedImagesRef.current += 1;
    const loadedImages = loadedImagesRef.current;
    const totalImages = searchResult.rows.items.length;
    const group = imageGroupRef.current;
    if (!group) {
      return;
    }
    if (loadedImages >= totalImages || loadedImages % 100 === 0) {
      group.clearCache();
      group.cache();
      group.getLayer()?.batchDraw();
    }
  }, [searchResult.rows.items.length]);

  const hideAutoScores = useRMSelector(selectHideAutoScores);
  const widthPerRect = 0.12;
  const heightPerRect = 0.12;

  const images = useMemo(() => {
    return searchResult.rows.items.map((sr, i) => {
      //@ts-ignore
      const rect = wellRectangles[i].props;

      return (
        <WellDropImageLoader
          key={`wdropImageId-${i}`}
          canvasCoordinatesToRenderOn={{ x: rect.x + 1, y: rect.y }} // adjusting padding
          dimensions={{ width: rect.width - 2, height: rect.height }} // adjusting padding
          wellNumber={sr[`Well.Number`]}
          captureProfileId={sr[`Capture Result.Profile ID`]}
          batchId={sr[`Capture Result.Image Batch ID`]}
          plateId={sr[`Experiment Plate.ID`]}
          dropNumber={sr[`Well Drop.Number`]}
          regionId={sr[`Region.ID`]}
          handleOnImageLoad={handleOnImageLoad}
        />
      );
    });
  }, [searchResult.rows.items]);

  const wellScores = useMemo(() => {
    return searchResult.rows.items.map((sr, i) => {
      const _score =
        scores.scores[
          `${sr.__WellDropID ?? sr[`Well Drop.ID`]}-${
            sr[`Capture Result.Image Batch ID`]
          }`
        ] ?? [];
      const filteredScores = filterScores(scoresGroup, _score, hideAutoScores);
      if (filteredScores.length > 0) {
        const rect = wellRects?.[i];
        if (!rect) {
          return null;
        }
        const autoScores = filteredScores.filter((score) => score.isAutoScore === true);
        const manualScores = filteredScores.filter(
          (score) => score.isAutoScore === false,
        );
        const sortedManualScores = manualScores.sort(compareWithScoreGroupId);
        const sortedAutoScores = autoScores.sort(compareWithScoreGroupId);
        let sortedWellDropScores = [...sortedManualScores, ...sortedAutoScores];
        if (rect.w * widthPerRect * sortedWellDropScores.length > rect.w) {
          const maxNoOfScores = rect.w / (rect.w * widthPerRect);
          sortedWellDropScores = sortedWellDropScores.slice(0, maxNoOfScores);
        }
        return (
          <Scores
            key={`wdropScoreId-${i}`}
            scoresOfWellDrops={sortedWellDropScores}
            width={rect.w * widthPerRect}
            height={rect.h * heightPerRect}
            x={rect.x}
            y={rect.y}
            scale={0}
            wellView={true}
            sizeDetails={null}
          />
        );
      }
      return null;
    });
  }, [searchResult.rows.items, scores, scoresGroup, hideAutoScores, wellRects]);

  useLayoutEffect(() => {
    const group = scoresGroupRef.current;
    if (!group) {
      return;
    }
    const rafId = window.requestAnimationFrame(() => {
      group.clearCache();
      group.cache();
      group.getLayer()?.batchDraw();
    });
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [wellScores]);

  if (!wellRectangles || searchResult.isFetching) {
    return;
  }

  return (
    <>
      {wellRectangles}
      <Group id="image-canvas-wrapper" ref={imageGroupRef}>
        {images}
      </Group>
      <Group id="scores-canvas-wrapper" ref={scoresGroupRef}>
        {wellScores}
      </Group>
      <HitRectangles
        wellDrops={searchResult.rows.items}
        scores={scores.scores}
        wellRectangles={wellRectangles}
      />
    </>
  );
};

export default withCanvasContainer(ImageCanvas, 'imageCanvas');
